/**
 * Shared read-state helpers for the notification panels (doctor and pharmacy).
 *
 * The notification row carries TWO unrelated flags and confusing one for the other is what
 * made clicked notifications keep rendering as unread:
 *   • `status`  — FCM/email/SMS DELIVERY state (`pending | sent | delivered | failed`). It stays
 *                 `sent` forever; the backend deliberately does NOT overwrite it when the user
 *                 reads the notification, so a failed push keeps its real status.
 *   • `isRead`  — the USER's read state, set by the mark-read endpoint alongside `readAt`.
 *
 * The API returns TypeORM entity properties, so the read flag arrives as `isRead`; local
 * optimistic updates have historically written `is_read`. Both casings are accepted here so the
 * panels have one answer regardless of where the object came from.
 */

/** True when the user has NOT read this notification yet. */
export const isNotificationUnread = (notification) => {
  const read = notification?.isRead ?? notification?.is_read;
  if (read === true) return false;
  if (read === false) return true;
  // Very old rows predate the read flag; fall back to the legacy delivery-status heuristic.
  return notification?.status === "sent";
};

/**
 * The identifier to send to the mark-read / delete endpoints, and to key the list on.
 *
 * The numeric primary key is preferred because the backend resolves that first and every row
 * from the list endpoint has one. `recipient` is only a fallback: for REALTIME rows it happens
 * to hold the string notification id, but for push/email/SMS rows it holds an FCM token, email
 * address or phone number — none of which resolve to a notification, and an FCM token is shared
 * across rows, so using it as a React key produces duplicates.
 */
export const getNotificationId = (notification) =>
  notification?.id ??
  notification?.notification_id ??
  notification?.recipient ??
  null;
